import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useDefaultDataStore } from '../../stateManagers/store/useDefaultDataStore';
import { useSchoolDetailsStore } from '../../stateManagers/newStores/useSchoolDetailsStore';

interface ExamType {
  id: string;
  title: string;
  name: string;
  examOrder: number;
  status: 'active' | 'inactive';
}

type StatusFilter = 'all' | 'active' | 'inactive';

interface NewExamTypeRow {
  name: string;
  title: string;
  order?: number;
}

export default function ExamTypesSetup() {
  const activeSchool = useDefaultDataStore((state) => state.active_school);
  const { schoolDetails } = useSchoolDetailsStore();

  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [loading, setLoading] = useState(false);

  // selection + bulk actions
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [settingActiveId, setSettingActiveId] = useState<string | null>(null);
  const [bulkActing, setBulkActing] = useState(false);

  // inline add — multi-row, lives in the main component, POST /api/exam-types/bulk
  const [newExamTypes, setNewExamTypes] = useState<NewExamTypeRow[]>([
    { name: '', title: '', order: 1 },
  ]);
  const [adding, setAdding] = useState(false);

  // inline edit — one or more rows at once, PUT /api/exam-types/bulk
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
  const [editValues, setEditValues] = useState<Record<string, { title: string; name: string; examOrder: number }>>({});
  const [savingEdits, setSavingEdits] = useState(false);

  const toast = useRef<Toast>(null);

  const currentSession = schoolDetails?.current_session?.session || '';

  const styles = `
    @keyframes fadeInSlide {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes bulkBarIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
    .exam-row-checkbox {
      width: 16px; height: 16px; accent-color: #2855ae; cursor: pointer; flex-shrink: 0;
    }
    .status-filter-chip {
      padding: 6px 14px; border-radius: 12px; font-size: 12px; font-weight: 600; cursor: pointer;
      border: 1.5px solid #e5e7eb; background: #f3f4f6; color: #4b5563; transition: all 0.15s;
    }
    .status-filter-chip.active-filter { border-color: #2855ae; background: #e0f2fe; color: #0369a1; }
    .select-mode-btn {
      display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:10px;
      font-size:12px; font-weight:600; cursor:pointer; transition:all .2s; border:1.5px solid;
    }
    .select-mode-btn.off { background:white; color:#2855ae; border-color:#c7d2fe; }
    .select-mode-btn.off:hover { background:#eff6ff; }
    .select-mode-btn.on { background:#2855ae; color:white; border-color:#2855ae; }
    .bulk-action-bar {
      animation: bulkBarIn .22s ease;
      display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px;
      background: linear-gradient(135deg,#1e40af,#3b82f6);
      border-radius: 14px; padding: 12px 18px; margin-bottom: 16px;
      box-shadow: 0 4px 16px rgba(59,130,246,.3);
    }
    .bulk-count { font-size:13px; font-weight:700; color:white; }
    .bulk-action-btn {
      display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px;
      font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all .15s;
    }
    .bulk-action-btn:disabled { opacity: .5; cursor: not-allowed; }
    .bulk-action-btn.deactivate { background:#f59e0b; color:white; }
    .bulk-action-btn.deactivate:hover:not(:disabled) { background:#d97706; }
    .bulk-action-btn.primary { background:white; color:#1e40af; }
    .bulk-action-btn.primary:hover:not(:disabled) { background:#eff6ff; }
    .bulk-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
    .bulk-action-btn.ghost:hover:not(:disabled) { background:rgba(255,255,255,.25); }
    .row-action-btn {
      display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border-radius:8px;
      font-size:11px; font-weight:700; cursor:pointer; border:none; transition:all .15s; flex-shrink:0;
    }
    .row-action-btn:disabled { opacity:.5; cursor:not-allowed; }
    .row-action-btn.set-active { background:#e0f2fe; color:#0369a1; }
    .row-action-btn.set-active:hover:not(:disabled) { background:#bae6fd; }
    .row-action-btn.deactivate { background:#fef2f2; color:#dc2626; }
    .row-action-btn.deactivate:hover:not(:disabled) { background:#fee2e2; }
  `;

  const mapExamType = (item: any): ExamType => ({
    id: item.id || item._id,
    title: item.title,
    name: item.name,
    examOrder: item.exam_order ?? item.examOrder ?? 0,
    status: item.status,
  });

  const fetchExamTypes = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (activeSchool?.school_id) params.school_id = activeSchool.school_id;
      const response = await apiClient.get('/exam-types', { params });
      const data = response.data?.data ?? [];
      setExamTypes(
        data.map(mapExamType).sort((a: ExamType, b: ExamType) => a.examOrder - b.examOrder)
      );
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to load exam types',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamTypes();
  }, [activeSchool?.school_id]);

  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visible: ExamType[]) => {
    const visibleIds = visible.map((e) => e.id);
    const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    clearSelection();
  };

  // ─────────────────────────────────────────────────────────────
  // Inline edit — one row (pencil icon) or many rows at once (bulk
  // "Edit" button). Edits happen directly in the list, no modal.
  // ─────────────────────────────────────────────────────────────
  const startEditing = (list: ExamType[]) => {
    setEditingIds(new Set(list.map((e) => e.id)));
    setEditValues((prev) => {
      const next = { ...prev };
      list.forEach((e) => {
        next[e.id] = { title: e.title, name: e.name, examOrder: e.examOrder };
      });
      return next;
    });
  };

  const cancelEditing = () => {
    setEditingIds(new Set());
  };

  const updateEditField = (id: string, field: 'title' | 'name' | 'examOrder', value: string) => {
    setEditValues((prev) => {
      const current = prev[id];
      if (!current) return prev;
      if (field === 'examOrder') {
        const parsed = value === '' ? current.examOrder : Number(value);
        return { ...prev, [id]: { ...current, examOrder: Number.isNaN(parsed) ? current.examOrder : parsed } };
      }
      return { ...prev, [id]: { ...current, [field]: value } };
    });
  };

  // PUT /api/exam-types/bulk (BulkUpdateExamTypes) — saves every row
  // currently in editingIds in a single request.
  const handleSaveEdits = async () => {
    const targets = examTypes.filter((e) => editingIds.has(e.id));
    const trimmed = targets.map((e) => {
      const edit = editValues[e.id];
      return {
        id: e.id,
        title: (edit?.title ?? e.title).trim(),
        name: (edit?.name ?? e.name).trim(),
        order: edit?.examOrder ?? e.examOrder,
        status: e.status,
      };
    });

    const invalid = trimmed.some((row) => !row.title || !row.name);
    if (invalid) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Fields', detail: 'Title and name cannot be empty.', life: 3000 });
      return;
    }

    setSavingEdits(true);
    try {
      const response = await apiClient.put('/exam-types/bulk', { exam_types: trimmed });
      const results = response.data?.results ?? [];
      const errorCount = results.filter((item: any) => item.status === 'error').length;

      if (errorCount > 0) {
        const errors = results
          .filter((item: any) => item.status === 'error')
          .map((item: any) => item.message)
          .slice(0, 3)
          .join('; ');
        toast.current?.show({
          severity: 'warn',
          summary: 'Some Errors Occurred',
          detail: errors.length > 150 ? errors.substring(0, 150) + '...' : errors,
          life: 5000,
        });
        if (errorCount === trimmed.length) {
          setSavingEdits(false);
          return;
        }
      } else {
        toast.current?.show({ severity: 'success', summary: 'Updated', detail: `${trimmed.length} exam type(s) updated.`, life: 3000 });
      }

      setEditingIds(new Set());
      fetchExamTypes();
      clearSelection();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Failed',
        detail: error.response?.data?.message || 'Could not update exam types',
        life: 3000,
      });
    } finally {
      setSavingEdits(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Inline multi-row add — POST /api/exam-types/bulk (BulkCreateExamTypes)
  // Lives directly in the main component, not the modal.
  // ─────────────────────────────────────────────────────────────
  const handleAddRow = () => {
    setNewExamTypes((prev) => [...prev, { name: '', title: '', order: prev.length + 1 }]);
  };

  const handleRemoveRow = (index: number) => {
    setNewExamTypes((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleUpdateRow = (
    index: number,
    field: 'name' | 'title' | 'order',
    value: string
  ) => {
    setNewExamTypes((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (field === 'order') {
          const parsed = value === '' ? undefined : Number(value);
          return { ...row, order: Number.isNaN(parsed as number) ? row.order : parsed };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleSaveNewExamTypes = async () => {
    const trimmed = newExamTypes.map((row) => ({ name: row.name.trim(), title: row.title.trim(), order: row.order }));
    const invalid = trimmed.some((row) => !row.name || !row.title || row.order === undefined);
    if (invalid) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Fields', detail: 'Name, title, and order are required for every row.', life: 3000 });
      return;
    }

    setAdding(true);
    try {
      const payload: Record<string, any> = {
        exam_types: trimmed.map((row) => ({ name: row.name, title: row.title, exam_order: row.order })),
      };
      if (activeSchool?.school_id) payload.school_id = activeSchool.school_id;

      const response = await apiClient.post('/exam-types/bulk', payload);
      const results = response.data?.results ?? [];
      const successCount = results.filter((item: any) => item.status === 'success').length;
      const errorCount = results.filter((item: any) => item.status === 'error').length;

      if (successCount > 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'Added',
          detail: `${successCount} exam type(s) created successfully.${errorCount > 0 ? ` ${errorCount} failed.` : ''}`,
          life: 3500,
        });
        setNewExamTypes([{ name: '', title: '', order: 1 }]);
        fetchExamTypes();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'No exam types were created. Please check the rows.', life: 3000 });
      }

      if (errorCount > 0) {
        const errors = results
          .filter((item: any) => item.status === 'error')
          .map((item: any) => item.message)
          .slice(0, 3)
          .join('; ');
        toast.current?.show({ severity: 'warn', summary: 'Some Errors Occurred', detail: errors.length > 150 ? errors.substring(0, 150) + '...' : errors, life: 5000 });
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Failed',
        detail: error.response?.data?.message || 'Could not create exam types',
        life: 3000,
      });
    } finally {
      setAdding(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Set active — POST /api/exam-types/:id/set-active
  // Backend deactivates every other active exam type in the school,
  // so this is a single-select radio, not an independent toggle.
  // ─────────────────────────────────────────────────────────────
  const handleSetActive = async (examType: ExamType) => {
    if (examType.status === 'active') return;
    setSettingActiveId(examType.id);

    const prevExamTypes = examTypes;
    setExamTypes((prev) =>
      prev.map((e) => ({ ...e, status: e.id === examType.id ? 'active' : e.status === 'active' ? 'inactive' : e.status }))
    );

    try {
      await apiClient.post(`/exam-types/${examType.id}/set-active`);
      toast.current?.show({
        severity: 'success',
        summary: 'Set Active',
        detail: `"${examType.title}" is now the active exam type.`,
        life: 2500,
      });
    } catch (error: any) {
      setExamTypes(prevExamTypes);
      toast.current?.show({
        severity: 'error',
        summary: 'Failed',
        detail: error.response?.data?.message || 'Could not set exam type active',
        life: 3000,
      });
    } finally {
      setSettingActiveId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Deactivate — PUT /api/exam-types/bulk (single-item bulk update)
  // There's no dedicated single-deactivate endpoint, so this reuses
  // BulkUpdateExamTypes with one item and status: "inactive".
  // ─────────────────────────────────────────────────────────────
  const handleDeactivate = async (examType: ExamType) => {
    if (examType.status === 'inactive') return;
    setTogglingId(examType.id);

    const prevExamTypes = examTypes;
    setExamTypes((prev) => prev.map((e) => (e.id === examType.id ? { ...e, status: 'inactive' } : e)));

    try {
      await apiClient.put('/exam-types/bulk', {
        exam_types: [{ id: examType.id, title: examType.title, name: examType.name, order: examType.examOrder, status: 'inactive' }],
      });
      toast.current?.show({ severity: 'success', summary: 'Deactivated', detail: `"${examType.title}" is now inactive.`, life: 2500 });
    } catch (error: any) {
      setExamTypes(prevExamTypes);
      toast.current?.show({
        severity: 'error',
        summary: 'Failed',
        detail: error.response?.data?.message || 'Could not deactivate exam type',
        life: 3000,
      });
    } finally {
      setTogglingId(null);
    }
  };

  // bulk deactivate selected — PUT /api/exam-types/bulk
  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;
    setBulkActing(true);

    const targets = examTypes.filter((e) => selectedIds.has(e.id) && e.status === 'active');
    if (targets.length === 0) {
      setBulkActing(false);
      return;
    }

    try {
      await apiClient.put('/exam-types/bulk', {
        exam_types: targets.map((e) => ({ id: e.id, title: e.title, name: e.name, order: e.examOrder, status: 'inactive' })),
      });
      setExamTypes((prev) => prev.map((e) => (selectedIds.has(e.id) ? { ...e, status: 'inactive' } : e)));
      toast.current?.show({ severity: 'success', summary: 'Deactivated', detail: `${targets.length} exam type(s) updated.`, life: 3000 });
      clearSelection();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Bulk Update Failed',
        detail: error.response?.data?.message || 'Could not update exam types',
        life: 3000,
      });
    } finally {
      setBulkActing(false);
    }
  };

  const hasExamTypes = examTypes.length > 0;
  const visibleExamTypes = examTypes.filter((e) => {
    if (statusFilter === 'all') return true;
    return e.status === statusFilter;
  });

  const activeCount = examTypes.filter((e) => e.status === 'active').length;
  const inactiveCount = examTypes.filter((e) => e.status === 'inactive').length;

  const visibleIds = visibleExamTypes.map((e) => e.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const selectedExamTypes = examTypes.filter((e) => selectedIds.has(e.id));
  const anySelectedActive = selectedExamTypes.some((e) => e.status === 'active');

  return (
    <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>
      <style>{styles}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        borderRadius: '24px',
        padding: '32px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className="pi pi-file-edit" style={{ fontSize: '18px', color: 'white' }} />
          </div>
          <div>
            <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Exam Types Setup
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ background: 'rgba(74, 222, 128, 0.9)', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                {examTypes.length} Exam Types
              </span>
              {currentSession && (
                <span style={{ background: 'rgba(251,191,36,0.9)', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  Session: {currentSession}
                </span>
              )}
              <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                {new Date().toISOString().split('T')[0]}
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Info card */}
      {!hasExamTypes && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)', border: '2px solid #dbeafe' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', height: '32px' }}>
              <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0' }}>About Exam Types</h4>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
                Exam types (e.g. Unit Test, Half Yearly, Final Exam) apply school-wide. Only one exam type can be
                marked <strong>Active</strong> at a time — setting a new one active automatically deactivates the rest.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Inline multi-row add */}
      <div style={{ background: 'white', borderRadius: '20px', padding: '28px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="pi pi-plus" style={{ fontSize: '14px', color: 'white' }} />
          </div>
          <h3 style={{ color: '#2855ae', fontSize: '16px', fontWeight: '600', margin: '0' }}>Add New Exam Types</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {newExamTypes.map((row, index) => (
            <div
              key={index}
              style={{ background: '#f9fafb', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '16px' }}
            >
              <div style={{ minWidth: '40px', height: '40px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>
                {index + 1}
              </div>
              <div style={{ flex: 1, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Name (Identifier) *</label>
                  <InputText
                    value={row.name}
                    onChange={(e) => handleUpdateRow(index, 'name', e.target.value)}
                    placeholder="e.g., midterm, final"
                    maxLength={50}
                    disabled={adding}
                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                  />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Title (Display Name) *</label>
                  <InputText
                    value={row.title}
                    onChange={(e) => handleUpdateRow(index, 'title', e.target.value)}
                    placeholder="e.g., Midterm Examination"
                    maxLength={100}
                    disabled={adding}
                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                  />
                </div>
                <div style={{ flex: '0 0 100px', minWidth: '100px' }}>
                  <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Order *</label>
                  <InputText
                    type="number"
                    value={row.order === undefined ? '' : String(row.order)}
                    onChange={(e) => handleUpdateRow(index, 'order', e.target.value)}
                    placeholder="1"
                    disabled={adding}
                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                  />
                </div>
              </div>
              <button
                onClick={() => handleRemoveRow(index)}
                disabled={newExamTypes.length === 1 || adding}
                style={{
                  background: newExamTypes.length === 1 ? '#e5e7eb' : '#ef4444', border: 'none', borderRadius: '8px',
                  width: '32px', height: '32px', cursor: newExamTypes.length === 1 ? 'not-allowed' : 'pointer', color: 'white',
                }}
              >
                <i className="pi pi-trash" style={{ fontSize: '12px' }} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleAddRow}
            disabled={adding}
            style={{
              background: 'white', border: '2px solid #3b82f6', borderRadius: '12px', padding: '10px 20px',
              cursor: adding ? 'not-allowed' : 'pointer', color: '#3b82f6', fontSize: '14px', fontWeight: '600',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            <i className="pi pi-plus" /> Add Another
          </button>
          <button
            onClick={handleSaveNewExamTypes}
            disabled={adding}
            style={{
              background: adding ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none',
              borderRadius: '12px', padding: '10px 24px', cursor: adding ? 'not-allowed' : 'pointer', color: 'white',
              fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            {adding ? 'Saving...' : 'Save Exam Types'}
          </button>
        </div>
      </div>


      {/* Exam Types List */}
      {loading ? (
        <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '48px', color: '#2855ae' }} />
          <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>Loading exam types...</p>
        </div>
      ) : hasExamTypes ? (
        <div style={{ background: 'white', borderRadius: '20px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', animation: 'fadeInSlide 0.7s ease-out' }}>

          {/* Header row: title, status filter, select toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ color: '#2855ae', fontSize: '16px', fontWeight: '600', margin: 0 }}>
              Exam Types
              <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '13px', marginLeft: '8px' }}>
                ({activeCount} active, {inactiveCount} inactive)
              </span>
            </h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['all', 'active', 'inactive'] as StatusFilter[]).map((f) => (
                  <button
                    key={f}
                    className={`status-filter-chip ${statusFilter === f ? 'active-filter' : ''}`}
                    onClick={() => setStatusFilter(f)}
                  >
                    {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Inactive'}
                  </button>
                ))}
              </div>
              <button className={`select-mode-btn ${selectionMode ? 'on' : 'off'}`} onClick={toggleSelectionMode}>
                <i className="pi pi-check-square" style={{ fontSize: 12 }} />
                {selectionMode ? 'Exit Select' : 'Select'}
              </button>
            </div>
          </div>

          {/* Bulk action bar */}
          {someSelected && editingIds.size === 0 && (
            <div className="bulk-action-bar">
              <div className="bulk-count">
                {selectedIds.size} exam type{selectedIds.size !== 1 ? 's' : ''} selected
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="bulk-action-btn deactivate"
                  disabled={!anySelectedActive || bulkActing}
                  onClick={handleBulkDeactivate}
                >
                  <i className="pi pi-ban" style={{ fontSize: 12 }} /> Deactivate
                </button>
                <button className="bulk-action-btn primary" onClick={() => startEditing(selectedExamTypes)}>
                  <i className="pi pi-pencil" style={{ fontSize: 12 }} /> Edit
                </button>
                <button className="bulk-action-btn ghost" onClick={clearSelection}>
                  <i className="pi pi-times" style={{ fontSize: 12 }} /> Clear
                </button>
              </div>
            </div>
          )}

          {/* Edit-mode action bar — shown while one or more rows are being edited inline */}
          {editingIds.size > 0 && (
            <div className="bulk-action-bar" style={{ background: 'linear-gradient(135deg,#0f766e,#14b8a6)', boxShadow: '0 4px 16px rgba(20,184,166,.3)' }}>
              <div className="bulk-count">
                Editing {editingIds.size} exam type{editingIds.size !== 1 ? 's' : ''}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="bulk-action-btn primary" disabled={savingEdits} onClick={handleSaveEdits}>
                  {savingEdits ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 12 }} /> : <i className="pi pi-check" style={{ fontSize: 12 }} />}
                  {savingEdits ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="bulk-action-btn ghost" disabled={savingEdits} onClick={cancelEditing}>
                  <i className="pi pi-times" style={{ fontSize: 12 }} /> Cancel
                </button>
              </div>
            </div>
          )}

          {selectionMode && visibleExamTypes.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingLeft: 4 }}>
              <input
                type="checkbox"
                className="exam-row-checkbox"
                checked={allSelected}
                onChange={() => toggleSelectAll(visibleExamTypes)}
              />
              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Select all visible</span>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {visibleExamTypes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>
                No {statusFilter} exam types.
              </div>
            ) : (
              visibleExamTypes.map((examType, index) => {
                const isChecked = selectedIds.has(examType.id);
                const isActive = examType.status === 'active';
                const isSettingActive = settingActiveId === examType.id;
                const isDeactivating = togglingId === examType.id;
                const isEditing = editingIds.has(examType.id);
                const edit = editValues[examType.id];

                if (isEditing) {
                  return (
                    <div
                      key={examType.id}
                      style={{ background: '#f0fdfa', borderRadius: '12px', padding: '20px', border: '1.5px solid #2dd4bf', display: 'flex', alignItems: 'center', gap: '16px' }}
                    >
                      <div style={{ minWidth: '40px', height: '40px', background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                          <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Name (Identifier) *</label>
                          <InputText
                            value={edit?.name ?? examType.name}
                            onChange={(e) => updateEditField(examType.id, 'name', e.target.value)}
                            maxLength={50}
                            disabled={savingEdits}
                            style={{ width: '100%', border: '1px solid #2dd4bf', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                          />
                        </div>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                          <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Title (Display Name) *</label>
                          <InputText
                            value={edit?.title ?? examType.title}
                            onChange={(e) => updateEditField(examType.id, 'title', e.target.value)}
                            maxLength={100}
                            disabled={savingEdits}
                            style={{ width: '100%', border: '1px solid #2dd4bf', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                          />
                        </div>
                        <div style={{ flex: '0 0 100px', minWidth: '100px' }}>
                          <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Order *</label>
                          <InputText
                            type="number"
                            value={String(edit?.examOrder ?? examType.examOrder)}
                            onChange={(e) => updateEditField(examType.id, 'examOrder', e.target.value)}
                            disabled={savingEdits}
                            style={{ width: '100%', border: '1px solid #2dd4bf', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={handleSaveEdits}
                          disabled={savingEdits}
                          title="Save"
                          style={{ background: '#0f766e', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: savingEdits ? 'not-allowed' : 'pointer', color: 'white' }}
                        >
                          {savingEdits ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 12 }} /> : <i className="pi pi-check" style={{ fontSize: 12 }} />}
                        </button>
                        <button
                          onClick={cancelEditing}
                          disabled={savingEdits}
                          title="Cancel"
                          style={{ background: '#e5e7eb', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: savingEdits ? 'not-allowed' : 'pointer', color: '#4b5563' }}
                        >
                          <i className="pi pi-times" style={{ fontSize: 12 }} />
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={examType.id}
                    style={{
                      background: isChecked ? '#eff6ff' : isActive ? '#f9fafb' : '#fef2f2',
                      borderRadius: '12px',
                      padding: '14px 18px',
                      border: isChecked ? '1.5px solid #93c5fd' : isActive ? '1px solid #e5e7eb' : '1px solid #fecaca',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      transition: 'all 0.15s',
                      opacity: isActive ? 1 : 0.85,
                    }}
                  >
                    {selectionMode && (
                      <input
                        type="checkbox"
                        className="exam-row-checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelect(examType.id)}
                      />
                    )}

                    <div style={{
                      minWidth: '36px', height: '36px',
                      background: isActive ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#9ca3af',
                      borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: '600', fontSize: '13px', flexShrink: 0,
                    }}>
                      {examType.examOrder || index + 1}
                    </div>

                    <div style={{ flex: 1, maxWidth: '220px' }}>
                      <InputText
                        value={examType.title}
                        disabled
                        style={{
                          width: '100%', border: '1px solid #2855ae', borderRadius: '8px', padding: '9px 12px',
                          fontSize: '13px', background: '#f9fafb', color: '#1f2937', cursor: 'not-allowed',
                        }}
                      />
                    </div>

                    <span style={{ fontSize: '12px', color: '#6b7280', flexShrink: 0, fontFamily: 'monospace' }}>
                      {examType.name}
                    </span>

                    {/* Set-active / deactivate — single-select semantics, no independent toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#16a34a' : '#dc2626', minWidth: 52, textAlign: 'right' }}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                      {isActive ? (
                        <button
                          className="row-action-btn deactivate"
                          disabled={isDeactivating}
                          onClick={() => handleDeactivate(examType)}
                        >
                          {isDeactivating ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 11 }} /> : <i className="pi pi-ban" style={{ fontSize: 11 }} />}
                          Deactivate
                        </button>
                      ) : (
                        <button
                          className="row-action-btn set-active"
                          disabled={isSettingActive}
                          onClick={() => handleSetActive(examType)}
                        >
                          {isSettingActive ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 11 }} /> : <i className="pi pi-check-circle" style={{ fontSize: 11 }} />}
                          Set Active
                        </button>
                      )}
                    </div>

                    <button
                      onClick={() => startEditing([examType])}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6', transition: 'all 0.2s', padding: '4px', flexShrink: 0 }}
                      onMouseOver={(e) => { e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.color = '#3b82f6'; e.currentTarget.style.transform = 'scale(1)'; }}
                      title="Edit exam type"
                    >
                      <i className="pi pi-pencil" style={{ fontSize: '13px' }} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <i className="pi pi-inbox" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
          <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>No Exam Types Found</h3>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 20px 0' }}>Click "Add Exam Type" above to get started.</p>
        </div>
      )}
    </div>
  );
}